import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useUtils } from "@/app/hooks/useUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useOverBookingsPermisionListAPI } from "@/app/hooks/api_hooks/bookings/useOverBookingsPermisionListAPI";

const OverBookingPermisionListComponent = () => {
  const fetchPermisionList = useOverBookingsPermisionListAPI();
  const { NO_RECORD_OVERBOOKING_PERMISSION } = useImageConstants();
  const isOverBookingListLoading = useLoader("isOverBookingPermissionListInProgress");
  const { veticDateFormat, truncateString } = useUtils();
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const routeId = getRouteKey("SLOTS", "id");
  const { routeState } = useRouteInfo(routeId);
  const { overbookingPermisionList } = routeState.state as ISlotsState;

  useEffect(() => {
    fetchPermisionList();
  }, []);

  return (
    <div className="bg-white px-3 py-5 overflow-x-hidden">
      <div className="flex flex-col gap-3 sm:flex-row mb-2 sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-sm font-semibold leading-6 text-gray-900">Allowed Overbooking Staff List</h1>
        </div>
        <div className="flex items-start sm:w-auto">
          <SolidButton
            size="LG"
            asType="button"
            onButtonClick={() => navigate(getRouteKey("OVERBOOKING", "url"))}
            className="!bg-black hover:!bg-black focus:!bg-black !text-white rounded-lg !font-medium"
          >
            <span className="text-sm">Allow Overbooking</span>
          </SolidButton>
        </div>
      </div>
      <div className="flow-root">
        {isOverBookingListLoading ? (
          <TableSkeleton />
        ) : overbookingPermisionList?.length === 0 ? (
          <NoDataFoundComponent
            description="No Overbooking Has Been Allowed."
            message="No overbooking has been allowed to the staff in the system, please allow to see it in the list."
            imageURL={NO_RECORD_OVERBOOKING_PERMISSION}
          />
        ) : (
          overbookingPermisionList?.length > 0 && (
            <>
              <div className="space-y-3 md:hidden">
                {overbookingPermisionList.map((overbooking, idx) => (
                  <div key={overbooking.id} className="rounded-xl border border-[#ECECEC] bg-white p-3 shadow-sm">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold text-[#1F1F23]">#{idx + 1}</p>
                      <p className="text-xs font-medium text-[#6C727F]">
                        {veticDateFormat(overbooking?.allowed_date) || "-"}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <p className="text-xs text-[#6C727F]">
                        <span className="font-medium text-[#1F1F23]">City:</span> {overbooking.city_name || "-"}
                      </p>
                      <p className="text-xs text-[#6C727F]">
                        <span className="font-medium text-[#1F1F23]">Clinic:</span>{" "}
                        {truncateString(overbooking?.clinic?.name || "-", 20)}
                      </p>
                      <p className="text-xs text-[#6C727F]">
                        <span className="font-medium text-[#1F1F23]">Provider:</span>{" "}
                        {truncateString(overbooking?.given_for_staff?.name || "-", 20)}
                      </p>
                      <p className="text-xs text-[#6C727F]">
                        <span className="font-medium text-[#1F1F23]">User Name:</span>{" "}
                        {overbooking?.given_to_staff?.name || "-"}
                      </p>
                      <p className="text-xs text-[#6C727F]">
                        <span className="font-medium text-[#1F1F23]">Time:</span> {overbooking?.from_time || "-"} -{" "}
                        {overbooking?.to_time || "-"}
                      </p>
                      <p className="break-all text-xs text-[#6C727F]">
                        <span className="font-medium text-[#1F1F23]">Created By:</span>{" "}
                        {overbooking?.given_by_staff?.email || "-"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden md:block w-full max-w-full overflow-hidden">
                <div className="w-full max-w-full overflow-x-auto overscroll-x-contain">
                  <div className="inline-block min-w-full py-2 align-middle">
                    <table className="w-full min-w-[1200px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                        <tr>
                          <th
                            scope="col"
                            className="px-2 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            SN
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            City
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            Clinic
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            Date
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            Provider
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            User Name
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            Start Time
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            End Time
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23]"
                          >
                            Created By
                          </th>
                        </tr>
                      </thead>
                      <tbody className="w-full overflow-hidden rounded-xl">
                        {overbookingPermisionList.map((overbooking, idx) => {
                          const isFirst = idx === 0;
                          const isLast = idx === overbookingPermisionList.length - 1;
                          const isSingle = overbookingPermisionList.length === 1;
                          return (
                            <tr
                              key={overbooking.id}
                              className={`
                              bg-white
                              ${isSingle ? "rounded-xl" : ""}
                              ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                              ${isLast && !isSingle ? "rounded-b-xl" : ""}
                            `}
                            >
                              <td
                                className={`
                                px-2 py-3 whitespace-nowrap text-xs font-medium text-[#1F1F23]
                                ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                              `}
                              >
                                {idx + 1}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-medium text-[#1F1F23]">
                                {overbooking.city_name}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {truncateString(overbooking?.clinic?.name || "-", 20)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {veticDateFormat(overbooking?.allowed_date) || "-"}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {truncateString(overbooking?.given_for_staff?.name || "-", 20)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {overbooking?.given_to_staff?.name || "-"}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {overbooking?.from_time || "-"}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {overbooking?.to_time || "-"}
                              </td>
                              <td
                                className={`
                                px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]
                                ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                              `}
                              >
                                {overbooking?.given_by_staff?.email || "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default OverBookingPermisionListComponent;
